import type { RobotProfile } from '../profiles/types';

export type InputSource = 'training' | 'developer';

export interface ControlOutput {
  move: [number, number];
  yaw: number;
  /** Оператор сейчас удерживает управление (важно для dead-man switch) */
  held: boolean;
  source: InputSource;
}

/**
 * Контур управления оператора (п. 5 ТЗ).
 *
 * Training input — экранные органы как в реальном телеопе, основной режим.
 * Developer input — клавиатура, для разработки; в тренировочных упражнениях
 * отключается. Оба проходят один и тот же путь: мёртвая зона → кривая
 * чувствительности → плавный сброс при отпускании → отправка в канал.
 */
export class InputController {
  /** Положение экранного джойстика, -1..1 */
  private stick: [number, number] = [0, 0];
  private stickActive = false;
  private yawInput = 0;
  private yawActive = false;
  private dpad: { v: number; w: number } | null = null;
  private keys = new Set<string>();
  private keyboardEnabled = true;

  /** Сглаженный выход после сброса/нарастания */
  private outV = 0;
  private outW = 0;
  lastSource: InputSource = 'training';

  constructor(private profile: RobotProfile) {}

  setKeyboardEnabled(on: boolean): void {
    this.keyboardEnabled = on;
    if (!on) this.keys.clear();
  }

  get keyboardOn(): boolean {
    return this.keyboardEnabled;
  }

  bindKeyboard(target: Window): void {
    target.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.keys.add(e.code);
    });
    target.addEventListener('keyup', (e) => this.keys.delete(e.code));
    target.addEventListener('blur', () => this.keys.clear());
  }

  setStick(x: number, y: number): void {
    this.stick = [clamp(x), clamp(y)];
    this.stickActive = true;
    this.lastSource = 'training';
  }

  releaseStick(): void {
    this.stick = [0, 0];
    this.stickActive = false;
  }

  setYaw(v: number): void {
    this.yawInput = clamp(v);
    this.yawActive = Math.abs(v) > 0.001;
    this.lastSource = 'training';
  }

  releaseYaw(): void {
    this.yawInput = 0;
    this.yawActive = false;
  }

  /** Дискретная команда D-pad: удерживается, пока кнопка нажата */
  setDpad(v: number, w: number): void {
    this.dpad = { v, w };
    this.lastSource = 'training';
  }

  releaseDpad(): void {
    this.dpad = null;
  }

  /** Сырой ввод до кривых — для панели «текущие значения» */
  private raw(): { v: number; w: number; held: boolean } {
    if (this.dpad) return { v: this.dpad.v, w: this.dpad.w, held: true };

    let v = 0;
    let w = 0;
    let held = false;
    if (this.stickActive) {
      v = this.stick[1];
      w = this.stick[0] * 0.6; // отклонение джойстика вбок подруливает
      held = true;
    }
    if (this.yawActive) {
      w = this.yawInput;
      held = true;
    }

    if (this.keyboardEnabled) {
      const k = this.keys;
      let kv = 0;
      let kw = 0;
      if (k.has('KeyW') || k.has('ArrowUp')) kv += 1;
      if (k.has('KeyS') || k.has('ArrowDown')) kv -= 1;
      if (k.has('KeyA') || k.has('ArrowLeft')) kw -= 1;
      if (k.has('KeyD') || k.has('ArrowRight')) kw += 1;
      const limiter = k.has('ShiftLeft') || k.has('ShiftRight') ? 0.4 : 1;
      if (k.has('Space')) {
        kv = 0;
        kw = 0;
      }
      if (kv !== 0 || kw !== 0) {
        v = kv * limiter;
        w = kw * limiter;
        held = true;
        this.lastSource = 'developer';
      }
    }

    return { v, w, held };
  }

  /** dt в мс. Возвращает команду, которую оператор отправляет в канал. */
  update(dtMs: number): ControlOutput {
    const c = this.profile.control;
    const { v, w, held } = this.raw();

    let tv = curve(v, c.dead_zone, c.sensitivity_exp);
    let tw = curve(w, c.dead_zone, c.sensitivity_exp);

    // Dead-man switch: не удерживаешь — робот не едет
    if (c.dead_man_switch && !held) {
      tv = 0;
      tw = 0;
    }

    // Плавный сброс в ноль за release_ramp_ms (и такое же нарастание,
    // чтобы отпущенный джойстик не давал ступеньку в канал)
    const step = dtMs / Math.max(1, c.release_ramp_ms);
    this.outV = approach(this.outV, tv, step);
    this.outW = approach(this.outW, tw, step);

    return {
      move: [0, round3(this.outV)],
      yaw: round3(this.outW),
      held,
      source: this.lastSource,
    };
  }

  /** Мгновенный ноль: экстренный стоп и телепорт не ждут рампы */
  zero(): void {
    this.outV = 0;
    this.outW = 0;
    this.stick = [0, 0];
    this.stickActive = false;
    this.yawInput = 0;
    this.yawActive = false;
    this.dpad = null;
  }
}

function clamp(v: number): number {
  return Math.max(-1, Math.min(1, v));
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** Мёртвая зона + кривая чувствительности */
function curve(v: number, dead: number, exp: number): number {
  const a = Math.abs(v);
  if (a <= dead) return 0;
  const norm = (a - dead) / (1 - dead);
  return Math.sign(v) * Math.pow(norm, exp);
}

function approach(cur: number, target: number, step: number): number {
  const d = target - cur;
  if (Math.abs(d) <= step) return target;
  return cur + Math.sign(d) * step;
}
